import React from 'react'
import { useTranslation } from 'react-i18next'
import { NodeHtmlMarkdown } from 'node-html-markdown'
import { BeWizrMeeting, BeWizrMeetingOccurrence, RemoteImage } from '~/models'
import { observer } from '~/ui/component'
import {
  ClearButton,
  Empty,
  HBox,
  Hero,
  Label,
  Markdown,
  Panel,
  SVG,
  TextBlock,
  VBox,
} from '~/ui/components'
import { createUseStyles, layout } from '~/ui/styling'
import { BeWizrEnrollmentStatus, BeWizrSection } from '../bewizr'
import BeWizrMeetingEnrollmentForm from './BeWizrMeetingEnrollmentForm'
import BeWizrMeetingOccurrenceDateTimeView from './BeWizrMeetingOccurrenceDateTimeView'

export interface Props {
  meeting:     BeWizrMeeting
  occurrence?: BeWizrMeetingOccurrence
}

const BeWizrMeetingView = observer('BeWizrMeetingView', (props: Props) => {

  const [t] = useTranslation('bewizr-meetings')

  const {meeting, occurrence} = props

  const mayUpdateEnrollment = React.useMemo(() => {
    if (occurrence != null) {
      return occurrence.mayEnroll || occurrence.mayCancel
    } else {
      return meeting.occurrences.some(it => it.mayEnroll || it.mayCancel)
    }
  }, [occurrence, meeting.occurrences])

  const occurrenceWithEnrollment = React.useMemo(() => {
    if (occurrence == null) {
      return meeting.occurrences.find(it => it.isEnrolled || it.isOnWaitingList) ?? null
    } else {
      return occurrence.isEnrolled || occurrence.isOnWaitingList ? occurrence : null
    }
  }, [occurrence, meeting.occurrences])

  const featuredOccurrence = occurrence ?? occurrenceWithEnrollment ?? meeting.occurrences[0]

  const description = React.useMemo(() => {
    if (meeting.descriptionHtml == null || meeting.descriptionHtml.length === 0) { return null }
    // A little bit ridiculous, but this way we can use the styling from the <Markdown/> component
    return NodeHtmlMarkdown.translate(meeting.descriptionHtml)
  }, [meeting.descriptionHtml])


  const locationDescription = React.useMemo(() => {
    if (occurrence?.location == null || occurrence.location.descriptionHtml.length === 0) { return null }
    // A little bit ridiculous, but this way we can use the styling from the <Markdown/> component
    return NodeHtmlMarkdown.translate(occurrence.location.descriptionHtml)
  }, [occurrence])

  const image: RemoteImage | undefined = meeting.links.image == null ? undefined : {
    type: 'remote',
    url:   meeting.links.image,
  }

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <VBox>
        <Hero
          title={renderSummary()}
          image={image}
          backgroundImage={image}
        >
        </Hero>
        <VBox align='center' padding={layout.padding.m}>
          {renderContent()}
        </VBox>
      </VBox>
    )
  }

  function renderSummary() {
    if (featuredOccurrence == null) { return meeting.title }

    return (
      <VBox gap={layout.padding.l}>
        <VBox gap={layout.padding.inline.m} align='left'>
          <Label h1 truncate={false}>{meeting.title}</Label>
          {renderStatus()}
        </VBox>
        <VBox gap={layout.padding.inline.m}>
          {occurrence == null && occurrenceWithEnrollment == null && (
            <Label caption small classNames={$.next}>
              {t('next')}
            </Label>
          )}
          {renderDate()}
          {renderTime()}
          {renderFacilitator()}
          {renderOnline()}
          {renderLocation()}
        </VBox>
      </VBox>
    )
  }

  function renderStatus() {
    if (featuredOccurrence == null) { return null }
    if (occurrenceWithEnrollment == null && !featuredOccurrence.mayEnroll) { return null }

    return (
      <BeWizrEnrollmentStatus
        isEnrolled={featuredOccurrence.isEnrolled}
        isOnWaitingList={featuredOccurrence.isOnWaitingList}
        placesLeft={featuredOccurrence.placesLeft}
      />
    )
  }

  function renderDate() {
    return (
      <HBox gap={layout.padding.inline.m}>
        <SVG name='calendar' size={layout.icon.m}/>
        <BeWizrMeetingOccurrenceDateTimeView
          occurrence={featuredOccurrence}
          dateFormat='d LLLL yyyy'
          timeFormat={false}
        />
      </HBox>
    )
  }

  function renderTime() {
    return (
      <HBox gap={layout.padding.inline.m}>
        <SVG name='clock' size={layout.icon.m}/>
        <BeWizrMeetingOccurrenceDateTimeView
          occurrence={featuredOccurrence}
          dateFormat={false}
        />
      </HBox>
    )
  }

  function renderOnline() {
    const {isOnline} = featuredOccurrence
    if (!isOnline) { return null }

    return (
      <HBox gap={layout.padding.inline.m}>
        <SVG name='webcast' size={layout.icon.m}/>
        <Label>
          {t('online')}
        </Label>
      </HBox>
    )
  }

  function renderFacilitator() {
    const {facilitator} = featuredOccurrence
    if (facilitator == null) { return null}

    return (
      <HBox gap={layout.padding.inline.m}>
        <SVG name='trainer' size={layout.icon.m}/>
        <Label>
          {facilitator.fullName}
        </Label>
      </HBox>
    )
  }

  function renderLocation() {
    const {location} = featuredOccurrence
    if (location == null) { return null}

    return (
      <VBox gap={layout.padding.inline.s}>
        <HBox gap={layout.padding.inline.m}>
          <SVG name='map-pin' size={layout.icon.m}/>
          <Label>{location.title}</Label>
        </HBox>
        <VBox>
          <TextBlock classNames={$.address}>
            {[location.addressLine, `${location.addressZip} ${location.addressCity}`].join('\n')}
          </TextBlock>
        </VBox>
      </VBox>
    )
  }

  function renderContent() {
    return (
      <VBox classNames={$.content} gap={layout.padding.xl} paddingVertical={layout.padding.l}>
        <BeWizrSection title={t('description')}>
          {renderDescription()}
        </BeWizrSection>
        {locationDescription != null && (
          <BeWizrSection title={t('location.title')}>
            {renderLocationDescription()}
          </BeWizrSection>
        )}
        {!(occurrence != null && !occurrence.mayCancel && !occurrence.mayEnroll) && (
          <BeWizrSection title={t('enrollment.title')}>
            {renderEnroll()}
          </BeWizrSection>
        )}
      </VBox>
    )
  }

  function renderDescription() {
    if (description == null) { return null }

    return (
      <Markdown>
        {description}
      </Markdown>
    )
  }

  function renderLocationDescription() {
    if (occurrence?.location == null || locationDescription == null) { return null }
    const {location} = occurrence

    const href = `https://maps.google.com/?q=${encodeURIComponent([location.addressLine, location.addressZip, location.addressCity, location.addressCountry].join(', '))}`
    return (
      <VBox gap={layout.padding.l}>
        <Markdown>
          {locationDescription}
        </Markdown>
        <Panel padding={layout.padding.m} gap={layout.padding.m}>
          <Label h3>
            {location.title}
          </Label>
          <TextBlock>
            {[location.addressLine, `${location.addressZip} ${location.addressCity}`, location.addressCountry].join('\n')}
          </TextBlock>
          <HBox>
            <ClearButton
              href={href}
              icon='map'
              caption={t('location.show_on_map')}
            />
          </HBox>
        </Panel>
      </VBox>
    )
  }

  function renderEnroll() {
    if (meeting == null) { return null }

    return mayUpdateEnrollment ? (
      <BeWizrMeetingEnrollmentForm
        meeting={meeting}
        occurrence={occurrence}
      />
    ) : (
      <Panel>
        <Empty
          icon='info'
          title={t('enrollment.status.closed.full')}
          small
        />
      </Panel>
    )
  }

  return render()

})

export default BeWizrMeetingView

const useStyles = createUseStyles({
  content: {
    width:    layout.contentWidth,
    maxWidth: '100%',
  },

  address: {
    marginLeft: layout.icon.m.width + layout.padding.inline.m,
  },

  next: {
    marginBottom: layout.padding.inline.m,
  },
})